import { createReducer, PayloadAction } from '@reduxjs/toolkit';
import { isEmpty } from 'lodash';
import get from 'lodash/get';
import sortBy from 'lodash/sortBy';
import uniqBy from 'lodash/uniqBy';
import moment from 'moment-timezone';
import { ParsedQs } from 'qs';
import {
  IAutoQuote,
  IAutoCheckout,
  IAutoThirdPartyDataOrders,
  IDriver,
  IVehicle,
  IAutoEnrollmentApplication,
  IOktaAccessToken,
  IOktaIdToken,
  IOktaLoginInfo,
  IOktaRefreshedTokens,
  IUser,
  IRentersPolicy,
  IRentersQuote,
  IRentersQuoteDetails,
  IRentersQuoteApplicationVariables,
  IRentersCheckout,
  IRentersPurchaseInfo,
  IRentersQuotePlan,
  IQuoteUUID,
  IAutoVariables,
  IAutoVariablesPayload,
  IRentersVariablesPayload,
  IAutoPolicy,
  TPolicy,
  TPolicyHealthCheckErrorState,
} from 'sure-platform-sdk';

import ACTION_TYPES from '../constants/actionTypes';
import {
  ICmsContent,
  ICmsError,
  IFeatureFlag,
  IQuoteData,
  IQuoteHealthCheckState,
} from '../interfaces';

const Reducers = {
  authToken: createReducer<string | null>(null, (builder) =>
    builder
      .addCase(
        ACTION_TYPES.EXCHANGE_QUOTE_AUTH_TOKEN_SUCCESS,
        (_, action: PayloadAction<string>) => action.payload
      )
      .addCase(ACTION_TYPES.EXCHANGE_QUOTE_AUTH_TOKEN_FAILURE, () => null)
      .addCase(ACTION_TYPES.CLEAR_OKTA_FOR_QA, () => null)
      .addDefaultCase((state) => state)
  ),

  oktaIdToken: createReducer<IOktaIdToken | null>(null, (builder) =>
    builder
      .addCase(
        ACTION_TYPES.SAVE_OKTA_ID_TOKEN,
        (_, action: PayloadAction<IOktaIdToken>) => action.payload
      )
      .addCase(
        ACTION_TYPES.REFRESH_OKTA_TOKENS_SUCCESS,
        (_, action: PayloadAction<IOktaRefreshedTokens>) =>
          action.payload.idToken
      )
      .addCase(ACTION_TYPES.LOGOUT, () => null)
      .addCase(ACTION_TYPES.REFRESH_OKTA_TOKENS_FAILURE, () => null)
      .addCase(ACTION_TYPES.CLEAR_OKTA_FOR_QA, () => null)
      .addDefaultCase((state) => state)
  ),

  oktaAccessToken: createReducer<IOktaAccessToken | null>(null, (builder) => {
    builder
      .addCase(
        ACTION_TYPES.SAVE_OKTA_ACCESS_TOKEN,
        (_, action: PayloadAction<IOktaAccessToken>) => action.payload
      )
      .addCase(
        ACTION_TYPES.REFRESH_OKTA_TOKENS_SUCCESS,
        (_, action: PayloadAction<IOktaRefreshedTokens>) =>
          action.payload.accessToken
      )
      .addCase(ACTION_TYPES.LOGOUT, () => null)
      .addCase(ACTION_TYPES.REFRESH_OKTA_TOKENS_FAILURE, () => null)
      .addCase(ACTION_TYPES.CLEAR_OKTA_FOR_QA, () => null)
      .addDefaultCase((state) => state);
  }),

  oktaLoginInfo: createReducer<IOktaLoginInfo | null>(null, (builder) => {
    builder
      .addCase(
        ACTION_TYPES.CREATE_OKTA_LOGIN_INFO,
        (_, action: PayloadAction<IOktaLoginInfo>) => action.payload
      )
      .addCase(ACTION_TYPES.CLEAR_OKTA_LOGIN_INFO, () => null)
      .addCase(ACTION_TYPES.CLEAR_OKTA_FOR_QA, () => null)
      .addDefaultCase((state) => state);
  }),

  isCCPABannerOpen: createReducer<boolean>(true, (builder) => {
    builder
      .addCase(ACTION_TYPES.CLOSE_CCPA_BANNER, () => false)
      .addDefaultCase((state) => state);
  }),

  user: createReducer<IUser | null>(null, (builder) => {
    builder
      .addCase(
        ACTION_TYPES.GET_USER_SUCCESS,
        (_, action: PayloadAction<IUser>) => action.payload
      )
      .addCase(
        ACTION_TYPES.UPDATE_USER_SUCCESS,
        (_, action: PayloadAction<IUser>) => action.payload
      )
      .addCase(ACTION_TYPES.LOGOUT, () => null)
      .addCase(ACTION_TYPES.REFRESH_OKTA_TOKENS_FAILURE, () => null)
      .addDefaultCase((state) => state);
  }),

  plans: createReducer<IRentersQuotePlan[]>([], (builder) => {
    builder
      .addCase(
        ACTION_TYPES.GET_PLANS_SUCCESS,
        (_, action: PayloadAction<{ plans: IRentersQuotePlan[] }>) =>
          action.payload.plans
      )
      .addCase(ACTION_TYPES.FINALIZE_QUOTE_SUCCESS, () => [])
      .addCase(ACTION_TYPES.SAVE_OKTA_ID_TOKEN, () => [])
      .addCase(ACTION_TYPES.LOGOUT, () => [])
      .addCase(ACTION_TYPES.REFRESH_OKTA_TOKENS_FAILURE, () => [])
      .addDefaultCase((state) => state);
  }),

  enrollmentApplication: createReducer<IAutoEnrollmentApplication | null>(
    null,
    (builder) => {
      builder
        .addCase(
          ACTION_TYPES.UPDATE_ENROLLMENT_APPLICATION,
          (_, action: PayloadAction<IAutoEnrollmentApplication>) =>
            action.payload
        )
        .addCase(
          ACTION_TYPES.GET_AUTO_QUOTE_SUCCESS,
          (_, action: PayloadAction<IAutoQuote>) => {
            const application = action?.payload?.application;
            return {
              firstName: application?.client?.first_name || '',
              lastName: application?.client?.last_name || '',
              dob: application?.client?.dob || '',
              address: application?.rating_address || {},
              selfReportedIncidentCount:
                application.self_reported_incident_count,
              coverageEffectiveDate: application?.policy_effective_date || '',
            };
          }
        )
        .addCase(ACTION_TYPES.CLEAR_ENROLLMENT_APPLICATION, () => null)
        .addCase(ACTION_TYPES.LOGOUT, () => null)
        .addCase(ACTION_TYPES.REFRESH_OKTA_TOKENS_FAILURE, () => null)
        .addDefaultCase((state) => state);
    }
  ),

  quote: createReducer<IRentersQuote | IAutoQuote | null>(null, (builder) => {
    builder
      .addCase(
        ACTION_TYPES.GET_AUTO_QUOTE_SUCCESS,
        (_, action: PayloadAction<IAutoQuote>) => action.payload
      )
      .addCase(
        ACTION_TYPES.CREATE_AUTO_QUOTE_SUCCESS,
        (_, action: PayloadAction<IAutoQuote>) => action.payload
      )
      .addCase(
        ACTION_TYPES.UPDATE_AUTO_QUOTE_SUCCESS,
        (_, action: PayloadAction<IAutoQuote>) => action.payload
      )
      .addCase(
        ACTION_TYPES.CREATE_QUOTE_SUCCESS,
        (_, action: PayloadAction<{ quote: IRentersQuote }>) =>
          action.payload.quote
      )
      .addCase(
        ACTION_TYPES.UPDATE_QUOTE_SUCCESS,
        (_, action: PayloadAction<{ quote: IRentersQuote }>) =>
          action.payload.quote
      )
      .addCase(
        ACTION_TYPES.ADD_PAYMENT_METHOD_TO_QUOTE_SUCCESS,
        (_, action: PayloadAction<{ quote: IRentersQuote }>) =>
          action.payload.quote
      )
      .addCase(ACTION_TYPES.CLEAR_AUTO_QUOTE, () => null)
      .addCase(ACTION_TYPES.FINALIZE_QUOTE_SUCCESS, () => null)
      .addCase(ACTION_TYPES.LOGOUT, () => null)
      .addCase(ACTION_TYPES.REFRESH_OKTA_TOKENS_FAILURE, () => null)
      .addDefaultCase((state) => state);
  }),

  quoteData: createReducer<IQuoteData>(
    {
      isMakeChangesFlow: false,
      hasSeenAdjustedPriceModal: false,
      excluded_vehicles: [],
    },
    (builder) => {
      builder
        .addCase(ACTION_TYPES.CLEAR_AUTO_QUOTE, () => ({
          isMakeChangesFlow: false,
          hasSeenAdjustedPriceModal: false,
          excluded_vehicles: [],
        }))
        .addCase(
          ACTION_TYPES.UPDATE_QUOTE_DATA,
          (state, action: PayloadAction<Partial<IQuoteData>>) => ({
            ...state,
            ...action.payload,
          })
        )
        .addCase(ACTION_TYPES.EXCHANGE_QUOTE_AUTH_TOKEN_SUCCESS, () => ({
          isMakeChangesFlow: false,
          hasSeenAdjustedPriceModal: false,
          excluded_vehicles: [],
        }))
        .addCase(ACTION_TYPES.EXCHANGE_QUOTE_AUTH_TOKEN_FAILURE, () => ({
          isMakeChangesFlow: false,
          hasSeenAdjustedPriceModal: false,
          excluded_vehicles: [],
        }));
    }
  ),

  quoteVars: createReducer<Partial<IRentersVariablesPayload> | IAutoVariables>(
    {},
    (builder) => {
      builder
        .addCase(
          ACTION_TYPES.GET_QUOTE_VARS_SUCCESS,
          (_, action: PayloadAction<IRentersVariablesPayload>) => action.payload
        )
        .addCase(
          ACTION_TYPES.GET_AUTO_QUOTE_VARS_SUCCESS,
          (_, action: PayloadAction<IAutoVariablesPayload>) =>
            action.payload.variables
        )
        .addCase(ACTION_TYPES.CREATE_QUOTE_SUCCESS, () => {})
        .addCase(ACTION_TYPES.FINALIZE_QUOTE_SUCCESS, () => {})
        .addCase(ACTION_TYPES.FINALIZE_AUTO_QUOTE_SUCCESS, () => {})
        .addCase(ACTION_TYPES.CLEAR_AUTO_QUOTE, () => ({}))
        .addCase(ACTION_TYPES.SAVE_OKTA_ID_TOKEN, () => {})
        .addCase(ACTION_TYPES.LOGOUT, () => {})
        .addCase(ACTION_TYPES.REFRESH_OKTA_TOKENS_FAILURE, () => {})
        .addCase(ACTION_TYPES.EXCHANGE_QUOTE_AUTH_TOKEN_SUCCESS, () => ({}))
        .addCase(ACTION_TYPES.EXCHANGE_QUOTE_AUTH_TOKEN_FAILURE, () => ({}))
        .addDefaultCase((state) => state);
    }
  ),

  quoteExtraData: createReducer<{ [key: string]: any } | null>(
    null,
    (builder) => {
      builder
        .addCase(
          ACTION_TYPES.GET_AUTO_QUOTE_VARS_SUCCESS,
          (_, action: PayloadAction<IAutoVariablesPayload>) =>
            action.payload.extra_data
        )
        .addCase(ACTION_TYPES.CLEAR_AUTO_QUOTE, () => null)
        .addCase(ACTION_TYPES.LOGOUT, () => null)
        .addCase(ACTION_TYPES.REFRESH_OKTA_TOKENS_FAILURE, () => null)
        .addDefaultCase((state) => state);
    }
  ),

  quoteDetails: createReducer<Partial<IRentersQuoteDetails>>({}, (builder) => {
    builder
      .addCase(
        ACTION_TYPES.UPDATE_QUOTE_DETAILS,
        (state, action: PayloadAction<Partial<IRentersQuoteDetails>>) => {
          return { ...state, ...action.payload };
        }
      )
      .addCase(ACTION_TYPES.EXCHANGE_QUOTE_AUTH_TOKEN_SUCCESS, () => ({}))
      .addCase(ACTION_TYPES.FINALIZE_QUOTE_SUCCESS, () => ({}))
      .addCase(ACTION_TYPES.SAVE_OKTA_ID_TOKEN, () => ({}))
      .addCase(ACTION_TYPES.LOGOUT, () => ({}))
      .addCase(ACTION_TYPES.REFRESH_OKTA_TOKENS_FAILURE, () => ({}))
      .addDefaultCase((state) => state);
  }),

  variableSelections: createReducer<Partial<IRentersQuoteApplicationVariables>>(
    {},
    (builder) => {
      builder
        .addCase(
          ACTION_TYPES.UPDATE_VARIABLE_SELECTIONS,
          (
            state,
            action: PayloadAction<Partial<IRentersQuoteApplicationVariables>>
          ) => {
            return { ...state, ...action.payload };
          }
        )
        .addCase(
          ACTION_TYPES.UPDATE_QUOTE_SUCCESS,
          (state, action: PayloadAction<{ quote: IRentersQuote }>) => {
            return { ...state, ...action.payload.quote.application.variables };
          }
        )
        .addCase(ACTION_TYPES.FINALIZE_QUOTE_SUCCESS, () => {})
        .addCase(ACTION_TYPES.SAVE_OKTA_ID_TOKEN, () => {})
        .addCase(ACTION_TYPES.LOGOUT, () => {})
        .addCase(ACTION_TYPES.REFRESH_OKTA_TOKENS_FAILURE, () => {})
        .addDefaultCase((state) => state);
    }
  ),

  quoteCheckout: createReducer<
    Partial<IRentersCheckout | IAutoCheckout | null>
  >({}, (builder) => {
    builder
      .addCase(
        ACTION_TYPES.CHECKOUT_QUOTE_SUCCESS,
        (_, action: PayloadAction<IRentersCheckout>) => action.payload
      )
      .addCase(
        ACTION_TYPES.GET_AUTO_QUOTE_CHECKOUT_SUCCESS,
        (_, action: PayloadAction<IAutoCheckout>) => action.payload
      )
      .addCase(ACTION_TYPES.CLEAR_AUTO_QUOTE, () => null)
      .addCase(ACTION_TYPES.FINALIZE_QUOTE_SUCCESS, () => null)
      .addCase(ACTION_TYPES.FINALIZE_AUTO_QUOTE_SUCCESS, () => null)
      .addCase(ACTION_TYPES.LOGOUT, () => null)
      .addCase(ACTION_TYPES.REFRESH_OKTA_TOKENS_FAILURE, () => null)
      .addCase(ACTION_TYPES.EXCHANGE_QUOTE_AUTH_TOKEN_SUCCESS, () => null)
      .addCase(ACTION_TYPES.EXCHANGE_QUOTE_AUTH_TOKEN_FAILURE, () => null)
      .addDefaultCase((state) => state);
  }),

  quoteHealthCheckCachedDrivers: createReducer<IDriver[] | null>(
    null,
    (builder) => {
      builder
        .addCase(
          ACTION_TYPES.UPDATE_CACHED_QUOTE_HEALTH_CHECK_STATE,
          (state, action: PayloadAction<IQuoteHealthCheckState>) => {
            return action.payload.drivers || state;
          }
        )
        .addCase(ACTION_TYPES.CREATE_AUTO_QUOTE_SUCCESS, () => null)
        .addCase(ACTION_TYPES.CLEAR_AUTO_QUOTE, () => null)
        .addCase(ACTION_TYPES.FINALIZE_AUTO_QUOTE_SUCCESS, () => null)
        .addCase(ACTION_TYPES.GET_POLICIES, () => null)
        .addCase(ACTION_TYPES.LOGOUT, () => null)
        .addCase(ACTION_TYPES.REFRESH_OKTA_TOKENS_FAILURE, () => null)
        .addDefaultCase((state) => state);
    }
  ),

  policyHealthCheckErrorDetails: createReducer<TPolicyHealthCheckErrorState | null>(
    null,
    (builder) => {
      builder
        .addCase(
          ACTION_TYPES.UPDATE_POLICY_HEALTH_CHECK_ERROR_DETAILS,
          (state, action: PayloadAction<TPolicyHealthCheckErrorState>) => {
            return action.payload || state;
          }
        )
        .addCase(
          ACTION_TYPES.CLEAR_POLICY_HEALTH_CHECK_ERROR_DETAILS,
          () => null
        )
        .addCase(ACTION_TYPES.CLEAR_STAGED_AUTO_POLICY, () => null)
        .addDefaultCase((state) => state);
    }
  ),

  quoteHealthCheckCachedVehicles: createReducer<IVehicle[] | null>(
    null,
    (builder) => {
      builder
        .addCase(
          ACTION_TYPES.UPDATE_CACHED_QUOTE_HEALTH_CHECK_STATE,
          (state, action: PayloadAction<IQuoteHealthCheckState>) => {
            return action.payload.vehicles || state;
          }
        )
        .addCase(ACTION_TYPES.CREATE_AUTO_QUOTE_SUCCESS, () => null)
        .addCase(ACTION_TYPES.CLEAR_AUTO_QUOTE, () => null)
        .addCase(ACTION_TYPES.FINALIZE_AUTO_QUOTE_SUCCESS, () => null)
        .addCase(ACTION_TYPES.GET_POLICIES, () => null)
        .addCase(ACTION_TYPES.LOGOUT, () => null)
        .addCase(ACTION_TYPES.REFRESH_OKTA_TOKENS_FAILURE, () => null)
        .addDefaultCase((state) => state);
    }
  ),

  quoteHealthCheckCachedOrders: createReducer<
    IAutoThirdPartyDataOrders[] | null
  >(null, (builder) => {
    builder
      .addCase(
        ACTION_TYPES.UPDATE_CACHED_QUOTE_HEALTH_CHECK_STATE,
        (state, action: PayloadAction<IQuoteHealthCheckState>) => {
          return action.payload.orders || state;
        }
      )
      .addCase(ACTION_TYPES.CREATE_AUTO_QUOTE_SUCCESS, () => null)
      .addCase(ACTION_TYPES.CLEAR_AUTO_QUOTE, () => null)
      .addCase(ACTION_TYPES.FINALIZE_AUTO_QUOTE_SUCCESS, () => null)
      .addCase(ACTION_TYPES.GET_POLICIES, () => null)
      .addCase(ACTION_TYPES.LOGOUT, () => null)
      .addCase(ACTION_TYPES.REFRESH_OKTA_TOKENS_FAILURE, () => null)
      .addDefaultCase((state) => state);
  }),

  purchaseInfo: createReducer<Partial<IRentersPurchaseInfo>>({}, (builder) => {
    builder
      .addCase(
        ACTION_TYPES.CHECKOUT_QUOTE_SUCCESS,
        (state, action: PayloadAction<IRentersCheckout>) => {
          return { ...state, quoteCheckout: action.payload };
        }
      )
      .addCase(
        ACTION_TYPES.FINALIZE_QUOTE_SUCCESS,
        (state, action: PayloadAction<{ quote: IRentersQuote }>) => ({
          ...state,
          quote: action.payload.quote || state.quote,
          purchasedAt: moment()
            .tz(
              get(action.payload.quote, 'application.rating_address.timezone')
            )
            .toISOString(),
        })
      )
      .addCase(ACTION_TYPES.LOGOUT, () => {})
      .addCase(ACTION_TYPES.REFRESH_OKTA_TOKENS_FAILURE, () => {})
      .addDefaultCase((state) => state);
  }),

  policies: createReducer<TPolicy[]>([], (builder) => {
    builder
      .addCase(
        ACTION_TYPES.GET_POLICIES_SUCCESS,
        (_, action: PayloadAction<{ policies: TPolicy[] }>) =>
          action.payload.policies
      )
      .addCase(
        ACTION_TYPES.UPDATE_POLICY_SUCCESS,
        (state, action: PayloadAction<{ policy: TPolicy }>) =>
          sortBy(
            uniqBy([action.payload.policy, ...state], 'id'),
            (policy) => -moment(policy.ends_at).valueOf()
          )
      )
      .addCase(
        ACTION_TYPES.CANCEL_POLICY_SUCCESS,
        (state, action: PayloadAction<{ policy: TPolicy }>) =>
          sortBy(
            uniqBy([action.payload.policy, ...state], 'id'),
            (policy) => -moment(policy.ends_at).valueOf()
          )
      )
      .addCase(ACTION_TYPES.CREATE_AUTO_QUOTE_SUCCESS, () => [])
      .addCase(ACTION_TYPES.LOGOUT, () => [])
      .addCase(ACTION_TYPES.REFRESH_OKTA_TOKENS_FAILURE, () => [])
      .addDefaultCase((state) => state);
  }),

  policy: createReducer<TPolicy | null>(null, (builder) => {
    builder
      .addCase(
        ACTION_TYPES.GET_POLICY_SUCCESS,
        (_, action: PayloadAction<{ policy: TPolicy }>) => action.payload.policy
      )
      .addCase(
        ACTION_TYPES.UPDATE_POLICY_SUCCESS,
        (_, action: PayloadAction<{ policy: TPolicy }>) => action.payload.policy
      )
      .addCase(
        ACTION_TYPES.CANCEL_POLICY_SUCCESS,
        (_, action: PayloadAction<{ policy: TPolicy }>) => action.payload.policy
      )
      .addCase(ACTION_TYPES.CREATE_AUTO_QUOTE_SUCCESS, () => null)
      .addCase(ACTION_TYPES.LOGOUT, () => null)
      .addCase(ACTION_TYPES.REFRESH_OKTA_TOKENS_FAILURE, () => null)
      .addDefaultCase((state) => state);
  }),

  policyVars: createReducer<
    Partial<IRentersVariablesPayload | IAutoVariablesPayload>
  >({}, (builder) => {
    builder
      .addCase(
        ACTION_TYPES.GET_POLICY_VARS_SUCCESS,
        (
          _,
          action: PayloadAction<
            Partial<IRentersVariablesPayload | IAutoVariablesPayload>
          >
        ) => action.payload
      )
      .addCase(ACTION_TYPES.CREATE_AUTO_QUOTE_SUCCESS, () => ({}))
      .addCase(ACTION_TYPES.LOGOUT, () => ({}))
      .addCase(ACTION_TYPES.REFRESH_OKTA_TOKENS_FAILURE, () => ({}))
      .addDefaultCase((state) => state);
  }),

  policyCheckout: createReducer<IRentersCheckout | IAutoCheckout | null>(
    null,
    (builder) => {
      builder
        .addCase(
          ACTION_TYPES.GET_POLICY_CHECKOUT_SUCCESS,
          (state, action: PayloadAction<IRentersCheckout | IAutoCheckout>) =>
            action.payload
        )
        .addCase(ACTION_TYPES.CREATE_AUTO_QUOTE_SUCCESS, () => null)
        .addCase(ACTION_TYPES.LOGOUT, () => null)
        .addCase(ACTION_TYPES.REFRESH_OKTA_TOKENS_FAILURE, () => null)
        .addCase(ACTION_TYPES.CLEAR_STAGED_AUTO_POLICY, () => null)
        .addDefaultCase((state) => state);
    }
  ),

  editingPolicy: createReducer<IRentersPolicy | null>(null, (builder) => {
    builder
      .addCase(
        ACTION_TYPES.UPDATE_EDITING_POLICY,
        (_, action: PayloadAction<{ policy: IRentersPolicy }>) =>
          action.payload.policy
      )
      .addCase(ACTION_TYPES.CLEAR_EDITING_POLICY, () => null)
      .addCase(ACTION_TYPES.LOGOUT, () => null)
      .addCase(ACTION_TYPES.REFRESH_OKTA_TOKENS_FAILURE, () => null)
      .addDefaultCase((state) => state);
  }),

  stagedAutoPolicy: createReducer<IAutoPolicy | {}>({}, (builder) => {
    builder
      .addCase(
        ACTION_TYPES.UPDATE_STAGED_AUTO_POLICY,
        (_, action: PayloadAction<{ policy: IAutoPolicy }>) =>
          action.payload.policy
      )
      .addCase(
        ACTION_TYPES.GET_POLICY_SUCCESS,
        (state, action: PayloadAction<{ policy: TPolicy }>) => {
          return isEmpty(state) ? {} : { ...action.payload.policy, ...state };
        }
      )
      .addCase(ACTION_TYPES.CLEAR_STAGED_AUTO_POLICY, () => ({}))
      .addCase(ACTION_TYPES.LOGOUT, () => ({}))
      .addCase(ACTION_TYPES.REFRESH_OKTA_TOKENS_FAILURE, () => ({}))
      .addDefaultCase((state) => state);
  }),

  urlParams: createReducer<ParsedQs>({}, (builder) => {
    builder.addDefaultCase((state) => state);
  }),

  uuid: createReducer<IQuoteUUID | null>(null, (builder) => {
    builder
      .addCase(
        ACTION_TYPES.FINALIZE_QUOTE,
        (_, action: PayloadAction<{ uuid: IQuoteUUID }>) => action.payload.uuid
      )
      .addCase(ACTION_TYPES.FINALIZE_QUOTE_SUCCESS, () => null)
      .addCase(ACTION_TYPES.FINALIZE_QUOTE_FAILURE, () => null)
      .addDefaultCase((state) => state);
  }),

  featureFlags: createReducer<IFeatureFlag[]>([], (builder) => {
    builder
      .addCase(
        ACTION_TYPES.GET_FEATURE_FLAGS_SUCCESS,
        (_, action: PayloadAction<IFeatureFlag[]>) => action.payload
      )
      .addDefaultCase((state) => state);
  }),

  cmsContent: createReducer<Record<string, ICmsContent | ICmsError>>(
    {},
    (builder) => {
      builder
        .addCase(
          ACTION_TYPES.GET_CMS_CONTENT_SUCCESS,
          (state, action: PayloadAction<ICmsContent>) => {
            return {
              ...state,
              [action.payload.slug]: action.payload,
            };
          }
        )
        .addCase(
          ACTION_TYPES.GET_CMS_CONTENT_FAILURE,
          (state, action: PayloadAction<ICmsError>) => {
            return {
              ...state,
              [action.payload.slug]: action.payload,
            };
          }
        );
    }
  ),

  hasLoggedInDuringPurchaseFlow: createReducer<boolean>(false, (builder) => {
    builder
      .addCase(
        ACTION_TYPES.UPDATE_HAS_LOGGED_IN_DURING_PF,
        (_, action: PayloadAction<boolean>) => action.payload
      )
      .addCase(ACTION_TYPES.CREATE_AUTO_QUOTE_SUCCESS, () => false)
      .addCase(ACTION_TYPES.CLEAR_AUTO_QUOTE, () => false)
      .addCase(ACTION_TYPES.LOGOUT, () => false)
      .addCase(ACTION_TYPES.REFRESH_OKTA_TOKENS_FAILURE, () => false)
      .addDefaultCase((state) => state);
  }),
};

export default Reducers;
